import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, MessageSquare, Send, Plus, Trash2, Edit,
  CheckCircle, XCircle, Clock, Filter, BarChart3, Users, Bell
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs'); // logs, templates, compose
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    channel: 'sms',
    category: 'general',
    subject: '',
    message: ''
  });
  
  // Compose state
  const [composeForm, setComposeForm] = useState({
    recipients: 'all',
    selectedCustomers: [],
    channel: 'sms',
    templateId: '',
    subject: '',
    message: ''
  });
  const [customers, setCustomers] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchTemplates();
    fetchCustomers();
  }, [filterChannel, filterStatus]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const params = new URLSearchParams();
      if (filterChannel) params.append('channel', filterChannel);
      if (filterStatus) params.append('status', filterStatus);
      params.append('days', '30');

      const response = await fetch(`${API_URL}/api/notifications/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setStats(data.stats || {});
      } else {
        toast.error('Failed to load notification logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error loading notification logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/notifications/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/customer-monitoring/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const initializeDefaultTemplates = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/notifications/templates/init`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Default templates initialized!');
        fetchTemplates();
      } else {
        toast.error('Failed to initialize templates');
      }
    } catch (error) {
      toast.error('Error initializing templates');
    }
  };

  const createTemplate = async () => {
    if (!templateForm.name || !templateForm.message) {
      toast.error('Name and message are required');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/notifications/templates`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success('Template created successfully!');
        setShowTemplateModal(false);
        setTemplateForm({ name: '', channel: 'sms', category: 'general', subject: '', message: '' });
        fetchTemplates();
      } else {
        toast.error('Failed to create template');
      }
    } catch (error) {
      toast.error('Error creating template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/notifications/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Error deleting template');
    }
  };

  const sendNotification = async () => {
    if (!composeForm.message) {
      toast.error('Message is required');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('monitoringAdminToken');
      
      const customerIds = composeForm.recipients === 'all' 
        ? ['all'] 
        : composeForm.selectedCustomers;

      const response = await fetch(`${API_URL}/api/notifications/send`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_ids: customerIds,
          channel: composeForm.channel,
          template_id: composeForm.templateId || null,
          subject: composeForm.subject || null,
          message: composeForm.message
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(data.message);
          setComposeForm({
            recipients: 'all',
            selectedCustomers: [],
            channel: 'sms',
            templateId: '',
            subject: '',
            message: ''
          });
          setActiveTab('logs');
          fetchLogs();
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error) {
      toast.error('Error sending notification');
    } finally {
      setSending(false);
    }
  };

  const loadTemplate = (template) => {
    setComposeForm({
      ...composeForm,
      channel: template.channel,
      subject: template.subject || '',
      message: template.message,
      templateId: template.id
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: { color: 'bg-blue-100 text-blue-700', icon: <Send className="h-3 w-3" /> },
      delivered: { color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
      failed: { color: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" /> },
      scheduled: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="h-3 w-3" /> }
    };
    const badge = badges[status] || badges.sent;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${badge.color}`}>
        {badge.icon}
        {status}
      </span>
    );
  };

  const getChannelIcon = (channel) => {
    const icons = {
      email: <Mail className="h-4 w-4" />,
      sms: <MessageSquare className="h-4 w-4" />,
      whatsapp: <MessageSquare className="h-4 w-4 text-green-600" />
    };
    return icons[channel] || icons.sms;
  };

  if (loading && activeTab === 'logs') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Bell className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading notification center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* Header */}
      <div className="mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Customer Notification Center
              </h1>
              <p className="text-indigo-100 text-sm mt-1">
                Manage SMS, Email & WhatsApp notifications
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Total Sent</div>
                  <div className="text-3xl font-bold">{stats.total || 0}</div>
                </div>
                <Send className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Delivered</div>
                  <div className="text-3xl font-bold">{stats.by_status?.delivered || 0}</div>
                </div>
                <CheckCircle className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Failed</div>
                  <div className="text-3xl font-bold">{stats.by_status?.failed || 0}</div>
                </div>
                <XCircle className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Delivery Rate</div>
                  <div className="text-3xl font-bold">{stats.delivery_rate || 0}%</div>
                </div>
                <BarChart3 className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4 flex gap-2">
        <Button
          onClick={() => setActiveTab('logs')}
          className={activeTab === 'logs' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-700'}
        >
          <Send className="h-4 w-4 mr-2" />
          Notification Logs
        </Button>
        <Button
          onClick={() => setActiveTab('templates')}
          className={activeTab === 'templates' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-700'}
        >
          <Edit className="h-4 w-4 mr-2" />
          Templates
        </Button>
        <Button
          onClick={() => setActiveTab('compose')}
          className={activeTab === 'compose' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-700'}
        >
          <Mail className="h-4 w-4 mr-2" />
          Compose & Send
        </Button>
      </div>

      {/* Notification Logs Tab */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-indigo-600" />
                Notification History (Last 30 Days)
              </CardTitle>
              <div className="flex gap-2">
                <select
                  value={filterChannel}
                  onChange={(e) => setFilterChannel(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date/Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Channel</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Recipient</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm">{log.customer_name}</div>
                          <div className="text-xs text-gray-500">{log.customer_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {getChannelIcon(log.channel)}
                            <span className="text-sm capitalize">{log.channel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{log.recipient}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm max-w-md truncate" title={log.message}>
                            {log.subject && <div className="font-semibold">{log.subject}</div>}
                            {log.message}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(log.status)}
                          {log.failed_reason && (
                            <div className="text-xs text-red-600 mt-1">{log.failed_reason}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Send className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No notifications sent yet</p>
                <p className="text-xs mt-1">Go to "Compose & Send" to send your first notification</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-indigo-600" />
                Notification Templates
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={initializeDefaultTemplates}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Initialize Defaults
                </Button>
                <Button
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateForm({ name: '', channel: 'sms', category: 'general', subject: '', message: '' });
                    setShowTemplateModal(true);
                  }}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template, idx) => (
                  <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getChannelIcon(template.channel)}
                        <h3 className="font-bold text-sm">{template.name}</h3>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => loadTemplate(template)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Use template"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {template.category}
                      </span>
                      <span className="text-xs ml-2 text-gray-500 capitalize">
                        {template.channel}
                      </span>
                    </div>
                    {template.subject && (
                      <div className="text-sm font-semibold mb-1">{template.subject}</div>
                    )}
                    <p className="text-sm text-gray-600">{template.message.substring(0, 100)}...</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Edit className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No templates yet</p>
                <p className="text-xs mt-1">Click "Initialize Defaults" to add pre-built templates</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Compose & Send Notification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients
                </label>
                <select
                  value={composeForm.recipients}
                  onChange={(e) => setComposeForm({ ...composeForm, recipients: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">All Customers ({customers.length})</option>
                  <option value="selected">Selected Customers</option>
                </select>
              </div>

              {/* Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="channel"
                      value="sms"
                      checked={composeForm.channel === 'sms'}
                      onChange={(e) => setComposeForm({ ...composeForm, channel: e.target.value })}
                    />
                    <MessageSquare className="h-4 w-4" />
                    SMS
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="channel"
                      value="email"
                      checked={composeForm.channel === 'email'}
                      onChange={(e) => setComposeForm({ ...composeForm, channel: e.target.value })}
                    />
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="channel"
                      value="whatsapp"
                      checked={composeForm.channel === 'whatsapp'}
                      onChange={(e) => setComposeForm({ ...composeForm, channel: e.target.value })}
                    />
                    <MessageSquare className="h-4 w-4 text-green-600" />
                    WhatsApp
                  </label>
                </div>
              </div>

              {/* Template Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Use Template (Optional)
                </label>
                <select
                  value={composeForm.templateId}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    if (template) loadTemplate(template);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">-- Select Template --</option>
                  {templates.filter(t => t.channel === composeForm.channel).map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>

              {/* Subject (Email only) */}
              {composeForm.channel === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Email subject..."
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={composeForm.message}
                  onChange={(e) => setComposeForm({ ...composeForm, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="6"
                  placeholder="Type your message here...

Available placeholders:
{customer_name}, {ip}, {email}, {phone}, {city}, {office}"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use placeholders like {'{customer_name}'} for personalization
                </p>
              </div>

              {/* Send Button */}
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setComposeForm({
                    recipients: 'all',
                    selectedCustomers: [],
                    channel: 'sms',
                    templateId: '',
                    subject: '',
                    message: ''
                  })}
                  variant="outline"
                >
                  Clear
                </Button>
                <Button
                  onClick={sendNotification}
                  disabled={sending}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  {sending ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Notification
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Create New Template</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Payment Reminder"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Channel
                  </label>
                  <select
                    value={templateForm.channel}
                    onChange={(e) => setTemplateForm({ ...templateForm, channel: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="general">General</option>
                    <option value="billing">Billing</option>
                    <option value="service">Service</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="fup">FUP</option>
                    <option value="onboarding">Onboarding</option>
                  </select>
                </div>
              </div>

              {templateForm.channel === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Email subject"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={templateForm.message}
                  onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="6"
                  placeholder="Template message..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setShowTemplateModal(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createTemplate}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  Create Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
